import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, PLAN_LABELS, PLAN_PRICES_GROSS_MONTH_CENTS, type BillingOrder, type DowngradePreview, type Plan } from '@kontor/shared';
import { api, ApiError } from '../api';
import { HELP_HOME } from '../links';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' });
const day = (iso: string) => dateFormat.format(new Date(`${iso}T12:00:00Z`));
const gb = (bytes: number) => `${Math.round(bytes / 1024 ** 3)} GB`;

/**
 * Herunterstufen auf ein kleineres Paket. Das Fenster erklärt zuerst ausführlich, was der Wechsel bedeutet: ab wann er gilt, was das Paket dann kostet, welche Bereiche und Verbindungen
 * wegfallen, welche Daten unwiderruflich gelöscht werden und welche aus gesetzlichen Gründen bleiben. Erst nach einem Haken bei „Ich habe die Folgen gelesen“ lässt sich der Wechsel
 * verbindlich vormerken. Bis zum Wechsel kann der Mandant ihn im Wirkia Konto zurücknehmen.
 */
export function DowngradeDialog({ plan, onClose, onDone }: { plan: Plan; onClose: () => void; onDone: (order: BillingOrder) => void }) {
  const client = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const [understood, setUnderstood] = useState(false);
  const preview = useQuery({ queryKey: ['downgrade-preview', plan], queryFn: async () => (await api<{ data: DowngradePreview }>('GET', `/billing/downgrade/preview?plan=${plan}`)).data, retry: false });
  const confirm = useMutation({
    mutationFn: async () => (await api<{ data: BillingOrder }>('POST', '/billing/downgrade', { plan, confirm: true })).data,
    onSuccess: (order) => {
      client.invalidateQueries({ queryKey: ['billing'] });
      onDone(order);
    },
  });

  useEffect(() => {
    const el = dialog.current;
    if (el && !el.open) el.showModal();
  }, []);

  const p = preview.data;
  const error = (preview.error ?? confirm.error) instanceof ApiError ? ((preview.error ?? confirm.error) as ApiError).message : preview.error || confirm.error ? 'Das hat nicht geklappt. Bitte versuchen Sie es noch einmal.' : null;
  const total = p ? p.deletions.reduce((n, d) => n + d.count, 0) : 0;

  return (
    <dialog
      ref={dialog}
      className="dialog dialog--wide"
      aria-labelledby="downgrade-title"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialog.current && !confirm.isPending) onClose();
      }}
    >
      <div className="dialog__body downgrade">
        <h2 id="downgrade-title">Auf {PLAN_LABELS[plan]} herunterstufen?</h2>
        {preview.isLoading && <p className="muted">Wird berechnet …</p>}
        {error && <div className="alert alert--error" role="alert">{error}</div>}

        {p && (
          <>
            <p>
              Bitte lesen Sie genau, was dieser Schritt bedeutet. Der Wechsel gilt <strong>ab dem {day(p.effectiveOn)}</strong>, dem Beginn Ihres nächsten Abrechnungszeitraums. Bis dahin nutzen und bezahlen Sie
              Ihr Paket {PLAN_LABELS[p.fromPlan]} unverändert.
            </p>

            <h3>Preis</h3>
            <dl className="facts downgrade__facts">
              <div><dt>Heute ({PLAN_LABELS[p.fromPlan]})</dt><dd>{formatCents(PLAN_PRICES_GROSS_MONTH_CENTS[p.fromPlan])} brutto je Benutzer und Monat ({formatCents(p.currentGrossCents)} für zwölf Monate)</dd></div>
              <div><dt>Ab dem {day(p.effectiveOn)} ({PLAN_LABELS[p.toPlan]})</dt><dd><strong>{formatCents(PLAN_PRICES_GROSS_MONTH_CENTS[p.toPlan])} brutto je Benutzer und Monat</strong> ({formatCents(p.newGrossCents)} für zwölf Monate)</dd></div>
            </dl>

            <h3>Was Ihnen danach nicht mehr zur Verfügung steht</h3>
            <ul className="downgrade__list">
              {p.lostModules.map((m) => <li key={m}>{m}</li>)}
              {p.lostIntegrations.length > 0 && <li>Verbindungen: {p.lostIntegrations.join(', ')}</li>}
              {p.storageToBytes < p.storageFromBytes && <li>Speicher im Paket: {gb(p.storageToBytes)} statt {gb(p.storageFromBytes)}</li>}
            </ul>

            <h3>Diese Daten werden unwiderruflich gelöscht</h3>
            {p.deletions.some((d) => d.count > 0) || p.lostIntegrations.length > 0 ? (
              <>
                <ul className="downgrade__list downgrade__list--danger">
                  {p.deletions.filter((d) => d.count > 0).map((d) => <li key={d.label}><strong>{d.count}</strong> {d.label}</li>)}
                  {p.files > 0 && <li><strong>{p.files}</strong> Dateien (Dokumente, Bilder, Anhänge dieser Bereiche)</li>}
                  {p.lostIntegrations.length > 0 && <li>Die gespeicherten Zugangsdaten der Verbindungen zu {p.lostIntegrations.join(', ')}</li>}
                </ul>
                <p className="small">Insgesamt {total} Einträge. Sie lassen sich nach dem Wechsel nicht wiederherstellen, auch nicht, wenn Sie später wieder ein größeres Paket buchen.</p>
              </>
            ) : (
              <p>Zurzeit sind in diesen Bereichen keine Daten gespeichert. Es geht also nichts verloren.</p>
            )}

            {p.retained.some((r) => r.count > 0) && (
              <>
                <h3>Das bleibt erhalten</h3>
                <p className="small">
                  Rechnungen und Buchungsbelege müssen Sie nach dem Gesetz mehrere Jahre aufbewahren (acht Jahre). Deshalb löscht Wirkia sie nicht. Sie sind nach dem Wechsel nicht mehr über das Backoffice erreichbar. Sichern Sie
                  sie vorher.
                </p>
                <ul className="downgrade__list">{p.retained.filter((r) => r.count > 0).map((r) => <li key={r.label}><strong>{r.count}</strong> {r.label}</li>)}</ul>
              </>
            )}

            <div className="alert alert--warn" role="note">
              <strong>Sichern Sie Ihre Daten vor dem Wechsel.</strong> Unter <a href="/import-export" target="_blank" rel="noopener noreferrer">Import und Export<span className="sr-only"> (öffnet in neuem Tab)</span></a> laden Sie alles als Datei
              herunter. Die Anleitung dazu: <a href={`${HELP_HOME}einrichtung/import-export/`} target="_blank" rel="noopener noreferrer">Daten importieren und exportieren<span className="sr-only"> (öffnet in neuem Tab)</span></a>.
            </div>

            <p>
              Bis zum {day(p.effectiveOn)} können Sie den Wechsel jederzeit in <strong>Einrichtung → Wirkia Konto</strong> mit einem Klick zurücknehmen. Danach ist er endgültig.
            </p>

            <label className="check downgrade__confirm">
              <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} disabled={confirm.isPending} />
              <span>Ich habe die Folgen gelesen. Ich verstehe, dass die genannten Daten ab dem {day(p.effectiveOn)} unwiderruflich gelöscht werden und mein Paket dann {formatCents(PLAN_PRICES_GROSS_MONTH_CENTS[p.toPlan])} brutto je Benutzer und Monat kostet.</span>
            </label>
          </>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn--secondary" onClick={onClose} disabled={confirm.isPending} autoFocus>Abbrechen</button>
          <button type="button" className="btn btn--danger" disabled={!p || !understood || confirm.isPending} onClick={() => confirm.mutate()}>
            {confirm.isPending ? 'Wird vorgemerkt …' : p ? `Verbindlich auf ${PLAN_LABELS[plan]} herunterstufen` : 'Herunterstufen'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
